/**
 * The quote editor, in the same shape as the Stripe Quote Generator: a Subscription section
 * (one plan + its screens, then add-ons) and a Hardware section that is US-only.
 *
 * It still edits the flat `QuoteRequestLine[]` the pricing engine reads — the sections are a
 * view over the lines by `kind`, so a deep link (prefill.ts) and the engine are unchanged.
 * Items are picked from the LIVE catalog rather than typed, and every amount comes from it:
 * there is no price field, only a per-line discount on subscription and add-on lines.
 */
import { Link } from 'react-router';
import { Plus, Trash2 } from 'lucide-react';
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@staff-app/ui';
import { formatMoney } from '../pricing/cents';
import { isOneTime } from '../pricing/addons';
import { ENTERPRISE_ALIAS, ENTERPRISE_MIN_SCREENS, resolvePlanPrice } from '../pricing/plans';
import type { PricedLine, QuoteCatalog, QuoteRequestLine } from '../pricing/types';

/** Radix reserves '' for "no selection", so "No subscription" needs a value of its own. */
const NONE = '__none__';

/** Where an international customer's hardware is quoted instead. */
export const INTL_REQUESTS_PATH = '/staff/order-devices/international';

/**
 * Keep a discount field's raw text usable while it is being typed: digits, at most one
 * decimal point, at most two decimals, capped at 100.
 *
 * Returns a STRING so partial input survives a re-render. Parsing to a number on every
 * keystroke is what once made a decimal impossible to type — "12." became 12 and the dot
 * vanished under the caret — and the same trap applies to "7.5%".
 */
export function sanitizePercent(input: string): string {
  let s = input.replace(/[^0-9.]/g, '');
  const dot = s.indexOf('.');
  if (dot !== -1) {
    const whole = s.slice(0, dot);
    const frac = s.slice(dot + 1).replace(/\./g, '').slice(0, 2);
    s = `${whole}.${frac}`;
  }
  if (s && Number(s) > 100) s = '100';
  return s;
}

/**
 * Hardware is quoted to US customers in USD only. Anything else goes through International
 * Requests, which prices freight per shipment.
 */
export function hardwareAllowed(currency: string, region: 'internal' | 'international'): boolean {
  return currency.toLowerCase() === 'usd' && region === 'internal';
}

interface SectionProps {
  lines: QuoteRequestLine[];
  catalog: QuoteCatalog;
  currency: string;
  /** Priced results; each row finds its own by `rowId`. */
  priced: PricedLine[];
  onChange: (id: string, patch: Partial<QuoteRequestLine>) => void;
  onRemove: (id: string) => void;
  onAdd: (line: Omit<QuoteRequestLine, 'id'>) => void;
}

// ─────────────────────────────────── Subscription ───────────────────────────────────

export function SubscriptionSection({
  lines,
  catalog,
  currency,
  priced,
  onChange,
  onRemove,
  onAdd,
}: SectionProps) {
  const planLines = lines.filter((l) => l.kind === 'subscription');
  const addOnLines = lines.filter((l) => l.kind === 'addon');
  const plan = planLines[0];
  const pricedFor = (id: string) => priced.find((p) => p.rowId === id);

  const planOptions = catalog.plans
    .filter((p) => p.alias || p._id)
    .flatMap((p) => {
      const r = resolvePlanPrice(p, currency, 'yearly');
      const priced = r.cents != null && !r.fallback;
      // Enterprise is offered only in the currencies its Stripe price carries.
      if (p.alias === ENTERPRISE_ALIAS && !priced) return [];
      const price = priced ? ` — ${formatMoney(r.cents, currency)}/screen/yr` : '';
      const min = p.alias === ENTERPRISE_ALIAS ? `, ${ENTERPRISE_MIN_SCREENS}+ screens` : '';
      return [{ value: p.alias || p._id, label: `${p.name || p._id}${price}${min}` }];
    });

  // An add-on sold only in some currencies is left out of the picker in the others; a row a
  // link already added stays, and the engine names why it cannot be priced.
  const addOnOptions = catalog.addOns
    .filter((a) => a.key)
    .filter(
      (a) => !a.limitCurrencies?.length || a.limitCurrencies.includes(currency.toLowerCase()),
    )
    .map((a) => ({ value: a.key, label: a.name || a.key }));

  const onPlan = (v: string) => {
    if (v === NONE) {
      // No plan ⇒ no add-ons either, as on the Stripe Quote Generator.
      [...planLines, ...addOnLines].forEach((l) => onRemove(l.id));
      return;
    }
    // Enterprise starts at its minimum so the operator never has to know the rule.
    const floor = v === ENTERPRISE_ALIAS ? ENTERPRISE_MIN_SCREENS : 1;
    if (plan) {
      onChange(plan.id, {
        sku: v,
        query: undefined,
        quantity: Math.max(plan.quantity ?? 0, floor),
      });
    } else {
      onAdd({ kind: 'subscription', sku: v, quantity: floor, term: 'yearly' });
    }
  };

  return (
    <section
      className="flex flex-col gap-3 rounded-lg border bg-card p-4 shadow-sm"
      data-testid="quote-subscription-card"
    >
      <div className="flex flex-col gap-1">
        <h2 className="text-sm font-semibold text-foreground">{'Subscription'}</h2>
        <p className="text-xs text-muted-foreground">
          {`Yearly, priced from the live catalog in ${currency.toUpperCase()}. The annual 10% is already in the price. Enterprise starts at ${ENTERPRISE_MIN_SCREENS} screens. Each line can carry its own discount.`}
        </p>
      </div>

      <div
        className="grid grid-cols-1 items-end gap-3 md:grid-cols-[2fr_1fr_1fr_8rem]"
        data-testid={plan ? `quote-line-${plan.id}` : undefined}
      >
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{'Plan'}</Label>
          <Select value={plan?.sku ?? NONE} onValueChange={onPlan}>
            <SelectTrigger className="cursor-pointer" data-testid="quote-plan">
              <SelectValue placeholder="No subscription" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE} className="cursor-pointer">
                {'No subscription'}
              </SelectItem>
              {planOptions.map((o) => (
                <SelectItem key={o.value} value={o.value} className="cursor-pointer">
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {plan ? (
          <>
            <QtyField line={plan} label="Screens" onChange={onChange} />
            <DiscountField line={plan} onChange={onChange} />
            <AmountCell line={plan} result={pricedFor(plan.id)} currency={currency} />
          </>
        ) : null}
      </div>

      {/* A link can carry more than one subscription line. Shown rather than dropped, so the
          quote never prices something the editor does not display. */}
      {planLines.slice(1).map((l) => (
        <ExtraRow key={l.id} line={l} result={pricedFor(l.id)} currency={currency} onRemove={onRemove} />
      ))}

      {plan || addOnLines.length ? (
        <div className="flex flex-col gap-2 border-t pt-3" data-testid="quote-addons">
          <p className="text-xs font-medium text-foreground">{'Add-ons'}</p>
          {addOnLines.map((line) => {
            const def = catalog.addOns.find((a) => a.key === line.sku);
            const flat = def ? isOneTime(def) : false;
            return (
              <div
                key={line.id}
                className="grid grid-cols-1 items-end gap-3 md:grid-cols-[2fr_1fr_1fr_1fr_8rem_2.5rem]"
                data-testid={`quote-line-${line.id}`}
              >
                <div className="flex flex-col gap-1.5">
                  <Label className="text-xs">{'Add-on'}</Label>
                  <Select
                    value={line.sku ?? ''}
                    onValueChange={(v) => {
                      const d = catalog.addOns.find((a) => a.key === v);
                      onChange(line.id, {
                        sku: v,
                        query: undefined,
                        tier: d?.defaultTier,
                        quantity: d && isOneTime(d) ? 1 : (line.quantity ?? plan?.quantity ?? 1),
                      });
                    }}
                  >
                    <SelectTrigger className="cursor-pointer" data-testid={`quote-line-${line.id}-sku`}>
                      <SelectValue placeholder="Choose" />
                    </SelectTrigger>
                    <SelectContent>
                      {addOnOptions.map((o) => (
                        <SelectItem key={o.value} value={o.value} className="cursor-pointer">
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {flat ? (
                  <div className="flex flex-col gap-1.5">
                    <Label className="text-xs">{'Screens'}</Label>
                    <Input value="1" disabled data-testid={`quote-line-${line.id}-qty`} />
                  </div>
                ) : (
                  <QtyField line={line} label="Screens" onChange={onChange} />
                )}
                {/* A tiered add-on is a different product per tier — OptiSound Standard is
                    $162 and Pro is $270 — so the tier has to be pickable, not implied. */}
                {def?.tiers?.length ? (
                  <div className="flex flex-col gap-1.5">
                    <Label className="text-xs">{'Tier'}</Label>
                    <Select
                      value={line.tier ?? def.defaultTier ?? ''}
                      onValueChange={(v) => onChange(line.id, { tier: v })}
                    >
                      <SelectTrigger className="cursor-pointer" data-testid={`quote-line-${line.id}-tier`}>
                        <SelectValue placeholder="Tier" />
                      </SelectTrigger>
                      <SelectContent>
                        {def.tiers.map((t) => (
                          <SelectItem key={t.key} value={t.key} className="cursor-pointer">
                            {t.name ?? t.key}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div />
                )}
                <DiscountField line={line} onChange={onChange} />
                <AmountCell line={line} result={pricedFor(line.id)} currency={currency} />
                <RemoveButton id={line.id} onRemove={onRemove} />
              </div>
            );
          })}
          <Button
            variant="outline"
            size="sm"
            className="w-fit cursor-pointer"
            onClick={() =>
              onAdd({ kind: 'addon', quantity: plan?.quantity ?? 1, term: 'yearly' })
            }
            data-testid="quote-add-addon"
          >
            <Plus className="mr-1 h-4 w-4" />
            {'Add add-on'}
          </Button>
        </div>
      ) : null}
    </section>
  );
}

// ───────────────────────────────────── Hardware ─────────────────────────────────────

export function HardwareSection({
  lines,
  currency,
  priced,
  onChange,
  onRemove,
  onAdd,
  allowed,
  options,
  catalogError,
}: SectionProps & {
  allowed: boolean;
  /** From the Stripe Quote Generator's hardware catalog; `value` is the option key. */
  options: { value: string; label: string }[];
  catalogError?: string | null;
}) {
  const deviceLines = lines.filter((l) => l.kind === 'device');
  const shipping = priced.filter((p) => p.kind === 'shipping');
  const pricedFor = (id: string) => priced.find((p) => p.rowId === id);


  return (
    <section
      className="flex flex-col gap-3 rounded-lg border bg-card p-4 shadow-sm"
      data-testid="quote-hardware-card"
    >
      <div className="flex flex-col gap-1">
        <h2 className="text-sm font-semibold text-foreground">{'Hardware (US only)'}</h2>
        <p className="text-xs text-muted-foreground">
          {'US customers, USD only. Prices and Standard shipping (including Kiosk bands) are the Stripe Quote Generator\'s. Hardware and shipping are never discounted.'}
        </p>
      </div>

      {catalogError ? (
        <p className="text-xs text-destructive">{`Could not load the hardware catalog: ${catalogError}`}</p>
      ) : null}

      {!allowed ? (
        <p className="text-xs text-muted-foreground" data-testid="quote-hardware-intl">
          {'Hardware for a customer outside the US, or billed in another currency, is quoted through '}
          <Link to={INTL_REQUESTS_PATH} className="font-medium text-primary underline-offset-2 hover:underline">
            {'International Requests'}
          </Link>
          {'.'}
          {deviceLines.length ? ' The hardware rows below are left off this quote.' : ''}
        </p>
      ) : null}

      {deviceLines.map((line) => (
        <div
          key={line.id}
          className={`grid grid-cols-1 items-end gap-3 md:grid-cols-[3fr_1fr_8rem_2.5rem] ${allowed ? '' : 'opacity-50'}`}
          data-testid={`quote-line-${line.id}`}
        >
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs">{'Product'}</Label>
            <Select
              value={line.sku ?? ''}
              onValueChange={(v) => onChange(line.id, { sku: v, query: undefined })}
              disabled={!allowed}
            >
              <SelectTrigger className="cursor-pointer" data-testid={`quote-line-${line.id}-sku`}>
                <SelectValue placeholder="Choose" />
              </SelectTrigger>
              <SelectContent>
                {options.map((o) => (
                  <SelectItem key={o.value} value={o.value} className="cursor-pointer">
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <QtyField line={line} label="Quantity" onChange={onChange} disabled={!allowed} />
          {allowed ? (
            <AmountCell line={line} result={pricedFor(line.id)} currency={currency} />
          ) : (
            <div />
          )}
          <RemoveButton id={line.id} onRemove={onRemove} />
        </div>
      ))}

      {allowed
        ? shipping.map((s, i) => (
            <div
              key={`ship-${i}`}
              className="grid grid-cols-1 gap-3 text-sm md:grid-cols-[3fr_1fr_8rem_2.5rem]"
              data-testid="quote-hardware-shipping"
            >
              <span className="text-muted-foreground">{`${s.label} (added automatically)`}</span>
              <span className="text-muted-foreground">{s.quantity}</span>
              <span className="text-right font-medium">
                {s.amountCents != null ? formatMoney(s.amountCents, currency) : 'unpriced'}
              </span>
              <span />
            </div>
          ))
        : null}

      <Button
        variant="outline"
        size="sm"
        className="w-fit cursor-pointer"
        disabled={!allowed}
        onClick={() => onAdd({ kind: 'device', quantity: 1 })}
        data-testid="quote-add-hardware"
      >
        <Plus className="mr-1 h-4 w-4" />
        {'Add hardware'}
      </Button>
    </section>
  );
}

// ───────────────────────────────────── cells ────────────────────────────────────────

function QtyField({
  line,
  label,
  onChange,
  disabled,
}: {
  line: QuoteRequestLine;
  label: string;
  onChange: SectionProps['onChange'];
  disabled?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-xs">{label}</Label>
      <Input
        value={line.quantity ?? ''}
        inputMode="numeric"
        disabled={disabled}
        onChange={(e) => {
          const digits = e.target.value.replace(/[^0-9]/g, '');
          onChange(line.id, { quantity: digits === '' ? null : Number(digits) });
        }}
        placeholder="1"
        data-testid={`quote-line-${line.id}-qty`}
      />
    </div>
  );
}

function DiscountField({
  line,
  onChange,
}: {
  line: QuoteRequestLine;
  onChange: SectionProps['onChange'];
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-xs">{'Disc %'}</Label>
      <Input
        value={line.discountPct ?? ''}
        // Raw string, not a parsed number — see QuoteRequestLine.discountPct.
        onChange={(e) => onChange(line.id, { discountPct: sanitizePercent(e.target.value) || null })}
        placeholder="0"
        title="Percentage off this line."
        data-testid={`quote-line-${line.id}-discountPct`}
      />
    </div>
  );
}

/**
 * What this line is charged. On a discounted line that is the NET, with the arithmetic
 * beneath it — the figure a rep repeats to a customer should be the one being billed.
 */
function AmountCell({
  line,
  result,
  currency,
}: {
  line: QuoteRequestLine;
  result: PricedLine | undefined;
  currency: string;
}) {
  return (
    <div className="flex flex-col items-end pb-2 text-sm">
      <div data-testid={`quote-line-${line.id}-amount`}>
        {result?.amountCents != null ? (
          <span className="font-medium">
            {formatMoney(result.amountCents - (result.discountCents ?? 0), currency)}
          </span>
        ) : (
          <span className="text-xs text-destructive">{'unpriced'}</span>
        )}
      </div>
      {result?.unitCents != null ? (
        <div className="text-[11px] text-muted-foreground" data-testid={`quote-line-${line.id}-unitPrice`}>
          {`${formatMoney(result.unitCents, currency)} each`}
        </div>
      ) : null}
      {result?.discountCents ? (
        <div className="text-[11px] text-muted-foreground" data-testid={`quote-line-${line.id}-breakdown`}>
          {`${formatMoney(result.amountCents ?? 0, currency)} − ${formatMoney(result.discountCents, currency)}`}
        </div>
      ) : null}
    </div>
  );
}

function ExtraRow({
  line,
  result,
  currency,
  onRemove,
}: {
  line: QuoteRequestLine;
  result: PricedLine | undefined;
  currency: string;
  onRemove: SectionProps['onRemove'];
}) {
  return (
    <div className="flex items-center justify-between gap-3 text-xs" data-testid={`quote-line-${line.id}`}>
      <span className="text-muted-foreground">{`Also on this quote: ${result?.label ?? line.sku ?? 'subscription'} × ${line.quantity ?? '?'}`}</span>
      <AmountCell line={line} result={result} currency={currency} />
      <RemoveButton id={line.id} onRemove={onRemove} />
    </div>
  );
}

function RemoveButton({ id, onRemove }: { id: string; onRemove: SectionProps['onRemove'] }) {
  return (
    <div className="flex items-center justify-end pb-1">
      <Button
        variant="ghost"
        size="icon-sm"
        onClick={() => onRemove(id)}
        className="cursor-pointer"
        aria-label="Remove line"
        data-testid={`quote-line-${id}-remove`}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}
